// NEW_FILE_JUSTIFIED: Replaces NotificationsModal (Dialog) with a Sheet slide-over panel. Old modal file deleted.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';
import { useOrgContext } from '@/hooks/useOrgContext';
import { groupNotificationsByDate } from '@/lib/notificationHelpers';
import { NotificationCardOrg } from '@/components/layout/NotificationCardOrg';
import { NotificationCardCustomer } from '@/components/layout/NotificationCardCustomer';
import type { INotificationsPanelProps, TNotificationCategory } from '@/@types/notificationPanel';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/

interface ITabConfig {
  // LOCAL_TYPE_JUSTIFIED: Tab config is only used within this component
  id: TNotificationCategory;
  label: string;
}

const ORG_TABS: ITabConfig[] = [
  { id: 'all', label: 'All' },
  { id: 'claims', label: 'Claims' },
  { id: 'policies', label: 'Policies' },
  { id: 'preauth', label: 'Pre-auth' },
  { id: 'invoices', label: 'Invoices' },
];

const CUSTOMER_TABS: ITabConfig[] = [
  { id: 'all', label: 'All' },
  { id: 'claims', label: 'Claims' },
  { id: 'policies', label: 'Policies' },
  { id: 'preauth', label: 'Pre-auth' },
];

export function NotificationsPanel({ open, onOpenChange }: INotificationsPanelProps) {
  const {
    notifications,
    unreadCount,
    unreadCountByCategory,
    activeCategory,
    setActiveCategory,
    markAllRead,
    acknowledge,
    isLoading,
    loadMore,
    hasMore,
    isLoadingMore,
  } = useNotifications();
  const { isOrgOwner } = useOrgContext();
  const navigate = useNavigate();

  const isOrg = isOrgOwner;
  const tabs = isOrg ? ORG_TABS : CUSTOMER_TABS;
  const dateGroups = groupNotificationsByDate(notifications);

  const handleNotificationClick = useCallback(
    (notificationId: Id<'insuranceNotifications'>, linkPath?: string) => {
      acknowledge(notificationId);
      if (linkPath) {
        onOpenChange(false);
        navigate(linkPath);
      }
    },
    [acknowledge, onOpenChange, navigate]
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 200;
      if (nearBottom && hasMore && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[420px] max-w-[100vw] p-0 flex flex-col gap-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="rounded-full px-2 py-0 text-xs font-semibold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllRead}
                className="text-sm font-medium text-primary hover:text-primary/80 h-auto px-2 py-1"
              >
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground h-9 w-9"
              aria-label="Close notifications"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1 px-4 py-0 border-b border-border overflow-x-auto">
          {tabs.map(tab => {
            const count = unreadCountByCategory[tab.id] ?? 0;
            const isActive = activeCategory === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategory(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-2.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-accent text-accent-foreground font-semibold'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                {tab.label}
                {count > 0 && (
                  <span
                    className={cn(
                      'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold',
                      isActive
                        ? 'bg-accent-foreground text-accent'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
          {isLoading && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading notifications...
            </div>
          )}

          {!isLoading && notifications.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">You're all caught up!</p>
              <p className="text-xs text-muted-foreground/60 mt-1">No notifications to show.</p>
            </div>
          )}

          {!isLoading &&
            dateGroups.map(group => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm px-6 py-2.5">
                  <span className="text-xs font-semibold text-muted-foreground tracking-wide">
                    {group.label}
                  </span>
                </div>

                {group.notifications.map(notification =>
                  isOrg ? (
                    <NotificationCardOrg
                      key={notification._id}
                      notification={notification}
                      onClick={handleNotificationClick}
                    />
                  ) : (
                    <NotificationCardCustomer
                      key={notification._id}
                      notification={notification}
                      onClick={handleNotificationClick}
                    />
                  )
                )}
              </div>
            ))}

          {isLoadingMore && (
            <div className="py-4 text-center text-xs text-muted-foreground">Loading more...</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
